import React, { useEffect, useRef, useState } from 'react'
import { Button, Col, Row } from 'antd'
import { v4 as uuidv4 } from 'uuid'
import { useNavigate } from 'react-router-dom'
import { useStores } from '../../../cls/stores/MobXProvider'
import { actLoadSpecificMonthMeetings } from '../../../stores/ui-lst-meetings/actions'
import { observer } from 'mobx-react-lite'
import {
	GeneralContentWrapper,
	GeneralTitle
} from '../../../components/Shared/GeneralComponents'
import { ArrowLeftOutlined } from '@ant-design/icons'
import CustomCalendar from '../../../components/Shared/CustomCalendar'
import dayjs from 'dayjs'
import { Filter } from '../../../components/Shared'
import { meetingProgressOptions } from './MeetingLst'
import { newMeeting } from '../../../stores/meetings/actions'
import { useSmartTranslation } from '../../../cls/TranslationContext'

/**
 * @component MeetingsCalendar
 * @description Displays a calendar-based view of meetings using a custom calendar component. Handles filtering, periodic synchronization, and supports monthly or weekly view modes. Users can navigate, create new meetings, and control data refresh.
 */
const MeetingsCalendar = observer(() => {
	const navigate = useNavigate()
	const { uiLstMeetings, hasIntervalLoad, appType, users } = useStores()
	const { t } = useSmartTranslation()
	const { appIsClient, appIsTeam } = appType
	const { loggedInUser } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	// Determine the table entity for notifications if needed.
	let tableEntity = appIsClient
		? 'meetings'
		: appIsTeam
		? 'meetings_team'
		: ''

	// If the user does not have permissions to read meetings, show a message.
	if (!(isAdmin || isSuperAdmin || users.canUser('read', tableEntity))) {
		return (
			<div style={{ textAlign: 'center' }}>
				{t('__denied.access').charAt(0).toUpperCase() +
					t('__denied.access%__feature-off%_meeting')
						.toLowerCase()
						.slice(1)}
			</div>
		)
	}

	const intervalIdRef = useRef(null)
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [errorKey, setErrorKey] = useState(null)
	const withIntervalLoad = hasIntervalLoad('meetings')

	// State to manage the current calendar date and panel type.
	const [currentDateObject, setCurrentDateObject] = useState(dayjs())
	const [calendarPanel, setCalendarPanel] = useState('month')

	// Extract filter values from the meetings UI list store.
	const bySearch = uiLstMeetings.search
	const byDel = uiLstMeetings.del
	const byType = uiLstMeetings.filter.meeting_type
	const byStatus = uiLstMeetings.filter.status
	const byPriority = uiLstMeetings.filter.priority
	const for_user_uid = uiLstMeetings.filter.for_user_uid
	const user_uid = uiLstMeetings.filter.user_uid
	const orderBy = uiLstMeetings.filter.orderBy
	const orderDir = uiLstMeetings.filter.orderDir

	useEffect(() => {
		// Set items per page if needed.
		uiLstMeetings.rpg = 25

		actLoadSpecificMonthMeetings(
			currentDateObject.unix(),
			calendarPanel
		).catch(console.error)

		// Function to cancel interval
		/**
		 * @function cancelCheckAndLoad
		 * @description Clears the interval responsible for polling updated meetings data. Called on component unmount or when polling is disabled.
		 */
		const cancelCheckAndLoad = () => {
			if (intervalIdRef.current) {
				clearInterval(intervalIdRef.current)
			}
		}

		// Function to check for modifications and reload if necessary
		/**
		 * @function checkAndLoadItems
		 * @description Triggers reloading of meetings for the current view if polling is enabled and no fatal error is present. Reloads data every 5 seconds when applicable.
		 */
		const checkAndLoadItems = () => {
			if (withIntervalLoad) {
				if (errorKey !== 'not_found') {
					actLoadSpecificMonthMeetings(true)
						.then(
							(res) =>
								res &&
								actLoadSpecificMonthMeetings().catch(
									console.error
								)
						)
						.catch(console.error)
				} else if (errorKey === 'not_found') {
					cancelCheckAndLoad()
				}
			} else {
				cancelCheckAndLoad()
			}
		}

		// Set up polling interval every 5 seconds
		if (withIntervalLoad) {
			intervalIdRef.current = setInterval(checkAndLoadItems, 5000)
		} else {
			cancelCheckAndLoad()
		}

		// Cleanup interval on unmount
		return () => cancelCheckAndLoad()
	}, [
		currentDateObject,
		calendarPanel,
		withIntervalLoad,
		bySearch,
		byDel,
		byType,
		byStatus,
		byPriority,
		user_uid,
		for_user_uid,
		orderBy,
		orderDir,
		appType.appIs
	])

	return (
		<GeneralContentWrapper>
			<Row justify="space-between" align="middle">
				<Col>
					<Button
						type="link"
						icon={<ArrowLeftOutlined />}
						onClick={() => navigate(-1)}
					>
						{t('single.back-to-list-view')}
					</Button>
					<GeneralTitle text={t('__planning%_meeting.multiple')} />
				</Col>
			</Row>

			<Filter
				uiStore={uiLstMeetings}
				addNew={() => {
					const newId = uuidv4()
					newMeeting(newId).then(() => {
						navigate(`/meetings/${newId}/edit`)
					})
				}}
				progressOptions={meetingProgressOptions}
			/>

			<CustomCalendar
				store={uiLstMeetings}
				onChange={(date) => setCurrentDateObject(date)}
				onPanelChange={(date, mode) => {
					setCalendarPanel(mode)
					setCurrentDateObject(date)
				}}
			/>
		</GeneralContentWrapper>
	)
})

export default MeetingsCalendar
